'use client';

import { Counter } from '@velocity-exchange/common';
import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import { SEMANTIC_STATUS } from '../components/Utils/SemanticChip';
import useVelocityStore from '../stores/VelocityStore/useVelocityStore';
import useDevStore from '../stores/useDevStore';
import { notify } from '../utils/notifications';
import useInterval from './useInterval';
import produce from 'immer';
import { useCurrentSlot } from 'src/hooks/useCurrentSlot';
import {
	useRiskCeilingSlotClock,
	useUserProtectionSlotClock,
} from 'src/hooks/useSlotClock';
import { SlotDurationMs, msToSlotsNum } from '@velocity-exchange/sdk';
import useModal from 'src/components/Modals/hooks/useModal';

// # Config
// Initial delay on startup before doing health checks
const INITIAL_DELAY_SECONDS = 10;

// Run a health check every 10 seconds
const HEALTH_CHECK_FREQUENCY_S = 10;

// A websocket slot feed never lands every slot, so the "ideal" update rate is
// deliberately slacker than the chain's. The old 500ms constant was this factor
// baked into the 400ms slot length; keeping it separate is what lets the rate
// track the chain as slots shorten instead of going blind-green.
const SLOT_FEED_SLACK_FACTOR = 1.25;

// Wall-clock intents for the bulk-account-loader / slot-subscriber divergence.
// The old scale derived these from the slacked rate, so "~2 seconds" actually
// fired at 1.6s; measuring a real slot delta needs no slack.
const SLOT_DELTA_AMBER_MS = 2_000;
const SLOT_DELTA_RED_MS = 4_000;

type HealthySide = 'greater' | 'lesser';

type HealthScale = {
	amber: number;
	red: number;
	healthySide: HealthySide;
};

// This health scale is used to check how many slot updates we think were missed during the check period. E.g. if the number of slot updates we get matches the expected count, we think we caught every single slot over the period and are at maximum health.
const getSlotUpdatesHealthScale = (
	slotDurationMs: SlotDurationMs
): HealthScale => {
	const expectedUpdatesPerCheck =
		(HEALTH_CHECK_FREQUENCY_S * 1000) /
		(slotDurationMs * SLOT_FEED_SLACK_FACTOR);

	return {
		// ~1/2 slots missed
		amber: expectedUpdatesPerCheck / 2,
		// ~3/4 slots missed
		red: expectedUpdatesPerCheck / 4,
		healthySide: 'greater',
	};
};

// This health scale is used to check the delta between the current slot of the bulk account loader and the slot subscriber. These two should remain in sync and if they fall out of sync it implies that something in the UI is not keeping up and won't be correctly displaying the current blockchain state.
const getSlotDeltaHealthScale = (
	slotDurationMs: SlotDurationMs
): HealthScale => ({
	amber: msToSlotsNum(SLOT_DELTA_AMBER_MS, slotDurationMs),
	red: msToSlotsNum(SLOT_DELTA_RED_MS, slotDurationMs),
	healthySide: 'lesser',
});

const getHealthStatusAgainstScale = (
	val: number,
	scale: HealthScale
): SEMANTIC_STATUS => {
	const isRed =
		scale.healthySide === 'lesser' ? val >= scale.red : val <= scale.red;
	if (isRed) return 'red';

	const isAmber =
		scale.healthySide === 'lesser' ? val >= scale.amber : val <= scale.amber;
	if (isAmber) return 'amber';

	return 'green';
};

// # RPC Health State
type State = {
	health: SEMANTIC_STATUS;
};

type Actions = {
	setHealth: (health: SEMANTIC_STATUS) => void;
};

interface RpcHealthStore extends State, Actions {}

const useCurrentRpcHealthStore = create<RpcHealthStore>((set) => {
	const setState = (fn: (s: State) => void) => set(produce(fn));

	return {
		health: 'green',
		setHealth: (health: SEMANTIC_STATUS) => {
			setState((state) => {
				state.health = health;
			});
		},
	};
});

export const useKeepCurrentRpcHealthInSync = () => {
	const currentSlot = useCurrentSlot();
	// The two arms tighten in opposite directions. The delta arm compares a slot
	// lag, so a longer assumed slot tightens it. The updates arm compares a count
	// against an expected rate, so a longer assumed slot expects fewer updates
	// and lowers the bar instead. Each takes the clock that guesses its own way.
	const { slotDurationMs } = useRiskCeilingSlotClock();
	const { slotDurationMs: updatesSlotDurationMs } =
		useUserProtectionSlotClock();
	const currentRpc = useVelocityStore((s) => s.rpcUrl);
	const rpcHealthStore = useCurrentRpcHealthStore();
	const currentBulkAccountLoader = useVelocityStore(
		(s) => s.currentBulkAccountLoader
	);
	const { show } = useModal();

	const showSettingsModal = () => {
		show({ type: 'settings', props: {} });
	};

	const delayExpired = useRef<boolean>(false);
	const slotUpdateCounter = useRef<Counter>(new Counter());
	const alreadyShownError = useRef<boolean>(false);

	// # Dev Stuff
	const devTriggerBadHealth = useDevStore((s) => s.triggerBadRpcHealth);

	const showHealthWarning = () => {
		notify({
			type: 'warning',
			message: 'Detected Connection Problems',
			description:
				'If you notice sustained problems using Velocity, try refreshing the page or changing to a different RPC Provider.',
			action: {
				label: 'Change RPC',
				callback: showSettingsModal,
			},
		});
		alreadyShownError.current = true;
	};

	const slotSubscriberHealthCheck = () => {
		const slotUpdateCount = slotUpdateCounter.current.get();

		slotUpdateCounter.current.reset();

		return getHealthStatusAgainstScale(
			slotUpdateCount,
			getSlotUpdatesHealthScale(updatesSlotDurationMs)
		);
	};

	const bulkAccountLoaderHealthCheck = (): SEMANTIC_STATUS => {
		try {
			const currentBulkLoaderSlot =
				currentBulkAccountLoader?.mostRecentSlot ?? 0;

			const deltaBetweenSlots = Math.abs(currentBulkLoaderSlot - currentSlot);

			return getHealthStatusAgainstScale(
				deltaBetweenSlots,
				getSlotDeltaHealthScale(slotDurationMs)
			);
		} catch {
			return 'red';
		}
	};

	// Show bad health for dev trigger
	useEffect(() => {
		if (devTriggerBadHealth) {
			rpcHealthStore.setHealth('red');
			showHealthWarning();
		}
	}, [devTriggerBadHealth]);

	// Turn off the initial delay gate
	useEffect(() => {
		const initialDelayTimeout = setTimeout(() => {
			delayExpired.current = true;
		}, INITIAL_DELAY_SECONDS * 1000);
		return () => clearTimeout(initialDelayTimeout);
	}, []);

	// Reset state when RPC Changes
	useEffect(() => {
		rpcHealthStore.setHealth('green');
	}, [currentRpc]);

	// Increment counter for every slot change
	useEffect(() => {
		slotUpdateCounter.current.increment();
	}, [currentSlot]);

	// Check rpc health results every 10 seconds
	useInterval(() => {
		if (!delayExpired.current) return;
		if (devTriggerBadHealth) return;

		const healthCheckResults = [
			slotSubscriberHealthCheck(),
			bulkAccountLoaderHealthCheck(),
		];

		if (healthCheckResults.includes('red')) {
			rpcHealthStore.setHealth('red');

			// temp disable rpc health warning
			// if (!alreadyShownError.current) {
			// 	showHealthWarning();
			// }
			return;
		}

		if (healthCheckResults.includes('amber')) {
			rpcHealthStore.setHealth('amber');

			return;
		}

		rpcHealthStore.setHealth('green');
	}, 1000 * HEALTH_CHECK_FREQUENCY_S);
};

// Hook to fetch RPC Health state
